"use client";
import React, { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  IconButton,
  Drawer,
  Divider,
} from "@mui/material";
import { useRouter, usePathname } from "next/navigation";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import i18n from "../i18n";

// ─── Styled Components (same pattern as AdsPageNavbar) ────────────────────────
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: "#0B0A10",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "80px",
  display: "flex",
  justifyContent: "center",
  zIndex: 1100,
  [theme.breakpoints.down("lg")]: {
    height: "60px",
  },
}));

const NavButton = styled(Button)(() => ({
  whiteSpace: "nowrap",
  color: "#A6A6A6",
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: 500,
  padding: "8px 14px",
  marginRight: "5px",
  borderRadius: 0,
  fontFamily: "var(--font-outfit)",
  "&:hover": {
    backgroundColor: "transparent",
    color: "#FFFFFF",
  },
  "&.active": {
    color: "#FFFFFF",
  },
}));

// Nav item used INSIDE the mobile drawer (dark text on white bg, full width row)
const MobileNavButton = styled(Button)(() => ({
  justifyContent: "flex-start",
  width: "100%",
  color: "#1A1A1A",
  textTransform: "none",
  fontSize: "0.95rem",
  fontWeight: 500,
  padding: "10px 4px",
  borderRadius: 0,
  fontFamily: "var(--font-outfit)",
  "&:hover": {
    backgroundColor: "transparent",
    color: "#7C3AED",
  },
  "&.active": {
    color: "#7C3AED",
  },
}));

const BookDemoButton = styled(Button)(({ theme }) => ({
  whiteSpace: "nowrap",
  backgroundColor: "#7C3AED",
  color: "#FFFFFF",
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 600,
  padding: "8px 20px",
  borderRadius: "8px",
  fontFamily: "var(--font-outfit)",
  lineHeight: "1.2",
  height: "42px",
  boxShadow: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease-in-out",

  [theme.breakpoints.down("md")]: {
    fontSize: "12px",
    padding: "6px 14px",
    height: "36px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
    padding: "5px 10px",
    height: "34px",
  },

  "&:hover": {
    background: "#7C3AED",
    transform: "scale(1.05)",
  },
  "&:active": { transform: "translateY(0)" },
}));

const LogoContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

const LANG_OPTIONS = [
  { value: "en", label: "English", countryCode: "us" },
  { value: "ar", label: "العربية", countryCode: "sa" },
];

// ─── Language Dropdown (same as AdsPageNavbar) ─────────────────────────────────
const CustomLangDropdown = ({
  currentLang,
  onChange,
  isRTL,
  fullWidth = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected =
    LANG_OPTIONS.find((o) => o.value === currentLang) || LANG_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        width: fullWidth ? "100%" : "auto",
        zIndex: 1300,
      }}
    >
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          height: { xs: "34px", sm: "36px", md: "38px", lg: "42px" },
          padding: { xs: "0 8px", sm: "0 10px", md: "0 12px" },
          borderRadius: "8px",
          border: "1.5px solid",
          borderColor: "#E0E0E0",
          backgroundColor: "#201430",
          cursor: "pointer",
          userSelect: "none",
          minWidth: { xs: "44px", sm: "110px", md: "130px", lg: "148px" },
          width: fullWidth ? "100%" : "auto",
          transition: "all 0.2s ease",
          boxSizing: "border-box",
          boxShadow: open ? "0 0 0 3px rgba(255,178,0,0.12)" : "none",
          "&:hover": {
            borderColor: "rgba(255,255,255,0.4)",
            backgroundColor: "#201430",
          },
        }}
      >
        {/* Flag */}
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <span
            className={`fi fi-${selected.countryCode}`}
            style={{ width: "20px", height: "15px", borderRadius: "2px" }}
          />
        </Box>

        {/* Label — hidden on xs, visible sm+ */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flex: 1,
            fontSize: { sm: "12px", md: "13px", lg: "14px" },
            fontWeight: 500,
            color: "#E0E0E0",
            fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
            whiteSpace: "nowrap",
            alignItems: "center",
          }}
        >
          {selected.label}
        </Box>

        {/* Chevron */}
        <Box sx={{ color: "#888", display: "flex", alignItems: "center" }}>
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      </Box>

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderRadius: "10px",
            border: "1.5px solid #F0F0F0",
            boxShadow: "0px 12px 32px rgba(0,0,0,0.12)",
            zIndex: 9999,
            overflow: "hidden",
            minWidth: "148px",
          }}
        >
          <Box
            sx={{ padding: "8px 14px 6px", borderBottom: "1px solid #F5F5F5" }}
          />
          {LANG_OPTIONS.map((option) => {
            const isActive = currentLang === option.value;
            return (
              <Box
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 14px",
                  cursor: "pointer",
                  backgroundColor: isActive ? "#FFFBF0" : "#fff",
                  borderLeft: isActive
                    ? "3px solid #FFB200"
                    : "3px solid transparent",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#FFFBF0" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <span
                    className={`fi fi-${option.countryCode}`}
                    style={{
                      width: "22px",
                      height: "16px",
                      borderRadius: "2px",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#FFB200" : "#333",
                    fontFamily:
                      option.value === "ar"
                        ? "var(--font-outfit)"
                        : "var(--font-outfit)",
                  }}
                >
                  {option.label}
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      backgroundColor: "#FFB200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6L5 9L10 3"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Nav Links for Leap page ───────────────────────────────────────────────
// "LEAP 2026" ab route change nahi karega — scrollTo id ke through
// LeapAboutSection tak smooth scroll karega (jis pe id="leap-2026-section" hai).
const NAV_LINKS = [
  {
    key: "LEAP 2026",
    translationKey: "navLeap.leap2026",
    scrollTo: "leap-2026-section",
  },
  {
    key: "Getgeek",
    translationKey: "navLeap.getgeek",
    scrollTo: "getgeek-section",
  },
  {
    key: "FAQ",
    translationKey: "navLeap.faqs",
    scrollTo: "faq-section",
  },
];

const LeappageNavbar = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [currentLang, setCurrentLang] = useState("en");
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    setCurrentLang(i18n.language || "en");
  }, []);

  const handleLanguageChange = (newLang) => {
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
    document.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
  };

  const isRTL = currentLang === "ar";

  // Handles both scroll-to-section links and normal route links.
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    }
    return false;
  };

  const handleNavClick = (link) => {
    if (link.scrollTo) {
      // If the target section already exists on the current page, just
      // scroll to it — do NOT navigate anywhere else.
      const scrolled = scrollToSection(link.scrollTo);

      if (!scrolled) {
        // Section not found on this page (e.g. user is on a completely
        // different route) — go to the Leap page first, then scroll
        // once it mounts.
        router.push("/leap");
        setTimeout(() => scrollToSection(link.scrollTo), 500);
      }
    } else if (link.route) {
      router.push(link.route);
    }
    setMobileOpen(false);
  };

  return (
    <StyledAppBar>
      <Container
        maxWidth="xl"
        sx={{
          padding: { xs: "0 12px", sm: "0 20px", md: "0 24px", lg: "0 20px" },
        }}
      >
        <Toolbar
          sx={{
            padding: "0 !important",
            minHeight: { xs: "56px", sm: "60px", lg: "80px" },
            justifyContent: "space-between",
          }}
        >
          {/* Logo - left side */}
          <LogoContainer
            sx={{ cursor: "pointer" }}
            onClick={() => router.push("/")}
          >
            <Image
              src={IMAGES.LOGOONE}
              alt="Getgeek Logo"
              width={100}
              height={40}
              style={{
                objectFit: "contain",
                maxWidth: "clamp(70px, 22vw, 100px)",
                maxHeight: "40px",
              }}
              priority
            />
          </LogoContainer>

          {/* Nav Links - hidden on mobile, visible md+ */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
            }}
          >
            {NAV_LINKS.map((link) => (
              <NavButton
                key={link.key}
                onClick={() => handleNavClick(link)}
                sx={{
                  fontFamily: isRTL
                    ? "var(--font-outfit)"
                    : "var(--font-outfit)",
                }}
              >
                {mounted ? i18n.t(link.translationKey) : link.key}
              </NavButton>
            ))}
          </Box>

          {/* Right Side: Language + Book Demo (always visible) + Hamburger (mobile only) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {/* Language dropdown - always visible */}
            <CustomLangDropdown
              currentLang={currentLang}
              onChange={handleLanguageChange}
              isRTL={isRTL}
            />

            {/* Book Demo button - always visible */}
            <BookDemoButton
              id="btn-leap-demo"
              variant="contained"
              // onClick={() => handleNavClick({ scrollTo: "leap-demo-section" })}
                onClick={() => window.dispatchEvent(new Event("open-leap-demo-modal"))}

              endIcon={
                isRTL ? (
                  <ArrowBackIcon
                    sx={{
                      mr: "60%",
                      fontSize: { xs: "14px", sm: "16px", lg: "inherit" },
                    }}
                  />
                ) : (
                  <ArrowForwardIcon
                    sx={{
                      fontSize: { xs: "14px", sm: "16px", lg: "inherit" },
                    }}
                  />
                )
              }
              sx={{
                fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
              }}
            >
              {mounted ? i18n.t("navLeap.bookDemo") : "Book a Demo"}
            </BookDemoButton>

            {/* Hamburger icon - visible only below md, opens drawer with nav links */}
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                display: { xs: "flex", md: "none" },
                color: "#FFFFFF",
              }}
              aria-label="open menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* ─── Mobile Drawer Menu ─────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            position: "fixed",
            top: { xs: "60px", sm: "70px" },
            right: "12px",
            left: "auto",
            bottom: "auto",
            width: "220px",
            maxHeight: "calc(100vh - 90px)",
            height: "auto",
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            padding: "12px 16px 16px",
            boxSizing: "border-box",
            boxShadow: "0px 12px 32px rgba(0,0,0,0.18)",
          },
        }}
      >
        {/* Close button row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "8px",
          }}
        >
          <IconButton
            onClick={() => setMobileOpen(false)}
            aria-label="close menu"
            size="small"
            sx={{ color: "#333" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Nav links */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {NAV_LINKS.map((link, idx) => (
            <React.Fragment key={link.key}>
              <MobileNavButton
                onClick={() => handleNavClick(link)}
                sx={{
                  fontFamily: isRTL
                    ? "var(--font-outfit)"
                    : "var(--font-outfit)",
                }}
              >
                {mounted ? i18n.t(link.translationKey) : link.key}
              </MobileNavButton>
              {idx < NAV_LINKS.length - 1 && (
                <Divider sx={{ borderColor: "#F0F0F0" }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Drawer>
    </StyledAppBar>
  );
};

export default LeappageNavbar;